const emptyCustomField = {
  is_sign: false,
  parent: null,
  sign: [],
  audio_description: [],
  transcript: [],
};

const trackStatus = Object.freeze({
  new: "new",
  draft: "draft",
  processing: "processing",
  uploaded: "uploaded",
  approved: "approved",
  disabled: "disabled",
  updating: "updating",
});

/**
 * Read a Brightcove video's `custom_fields.equalplay` for display.
 *
 * The field is a JSON *string*, and `JSON.parse` on it was unguarded in most
 * of the app: one malformed field took out whatever was rendering at the time,
 * including the whole sign language page, with nothing in the UI to say why.
 *
 * Returns null both when the field is absent and when it cannot be read —
 * callers that only display the contents treat those the same, and the console
 * warning is what distinguishes them when someone goes looking.
 *
 * Never use this before a write: a corrupt field reads as "no tracks", and
 * writing that back silently destroys every track on the video. Use
 * `requireEqpField` there instead.
 *
 * @param {object} video - a Brightcove video object, or any falsy value
 * @param {string} [context] - where this was called from, for the warning
 * @returns {object|null}
 */
const parseEqpField = (video, context) => {
  const raw = video?.custom_fields?.equalplay;
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (err) {
    console.warn(
      `Unreadable equalplay custom field on video ${video?.id ?? "?"}${
        context ? ` (${context})` : ""
      }`,
      err,
      raw
    );
    return null;
  }
};

/**
 * Read `custom_fields.equalplay` as the basis for writing it back.
 *
 * Differs from `parseEqpField` in exactly one way, and it is the important
 * one: a field that exists but cannot be parsed throws instead of reading as
 * empty. Every write path here is read-modify-write on the whole field, so
 * falling back to an empty object would replace a video's entire set of sign,
 * audio description and transcript tracks with nothing — the field being
 * corrupt is the one case where doing nothing is clearly right.
 *
 * An absent field is not an error: that is simply a video Equal Play has not
 * touched yet, and it starts from the empty shape.
 *
 * @param {object} video - a Brightcove video object
 * @param {string} [context] - where this was called from, for the message
 * @returns {object} the parsed field, or a fresh empty one
 * @throws {Error} if the field is present but not valid JSON
 */
const requireEqpField = (video, context) => {
  const raw = video?.custom_fields?.equalplay;
  // Fresh arrays, not a spread of the shared constant: a spread copies the
  // references, so a caller that pushed into `current.sign` would be pushing
  // into `emptyCustomField.sign` and poisoning every later read on the page.
  if (!raw) {
    return {
      ...emptyCustomField,
      sign: [],
      audio_description: [],
      transcript: [],
    };
  }

  try {
    return JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `The Equal Play data on video ${video?.id ?? "?"} could not be read, ` +
        `so it has not been changed. Please contact support.` +
        (context ? ` (${context})` : "")
    );
  }
};

export default emptyCustomField;
export { emptyCustomField, trackStatus, parseEqpField, requireEqpField };

// https://stream.wallenmedia.se/lab/plugin/newobject.html

// {
//     "is_sign": false,
//     "parent": "bcid",
//     "sign": [
//       {
//         "status": "draft, processing, uploaded, approved, disabled",
//         "id": "(bc video id)",
//         "label": "English(US)"
//       }
//     ],
//     "audio_description": [
//       {
//         "lang": "en- US",
//         "label": "English(US)",
//         "status": "draft, processing, uploaded, approved, disabled"
//       }
//     ],
//     "transcript": [
//       {
//         "lang": "en-US",
//         "Label": "English(US)",
//         "status": "draft, processing, uploaded, approved, disabled"
//       },
//       {
//         "lang": "en-UK",
//         "Label": "English(UK)",
//         "status": "draft, processing, uploaded, approved, disabled"
//       }
//     ]
//   }
